import {
  BorderRadius4,
  GreyGrey20,
  GreyGrey30,
  GreyGrey60,
  GreyGrey70,
  GreyGrey90,
  IndigoIndigo50,
  Primary13Regular,
  Primary13SemiBold,
  Skeleton,
  Center,
  Box,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import { customLocales, formatDistanceToNow } from '@sequencehq/utils/dates'
import { ReactNode } from 'react'

// How long a click waits for the preload to resolve before transitioning
// anyway, so a slow API never leaves the user staring at the list.
const PRELOAD_TRANSITION_TIMEOUT_MS = 500

const ListItemContentBase = (props: {
  icon: ReactNode
  title: ReactNode
  description: ReactNode
  createdAt?: string | number
  isSelected: boolean
  isUnread?: boolean
  statusIcon?: ReactNode
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  // Warms the data the item's preview needs and returns a promise. When
  // provided, the click defers onClick (the transition) until the preload
  // settles or PRELOAD_TRANSITION_TIMEOUT_MS elapses, so the destination
  // renders with its data ready. Owning this in the toolkit keeps each list's
  // click handler focused on navigation.
  onPreload?: () => Promise<unknown> | void
  'data-testid'?: string
}) => {
  const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const preload = props.onPreload?.()

    if (!preload) {
      props.onClick?.(e)
      return
    }

    // Stop propagation synchronously — the deferred handler below runs after
    // the event has finished bubbling.
    e.stopPropagation()
    void Promise.race([
      Promise.resolve(preload).catch(() => undefined),
      new Promise(resolve => setTimeout(resolve, PRELOAD_TRANSITION_TIMEOUT_MS))
    ]).then(() => props.onClick?.(e))
  }

  // Selected items always render at full emphasis (never dimmed), so the
  // active row stays legible against its GreyGrey20 background (WCAG AA).
  const isEmphasized = props.isUnread || props.isSelected

  return (
    <Grid
      role="group"
      data-testid={props['data-testid']}
      onClick={handleClick}
      gridRowGap="4px"
      padding="12px"
      height="60px"
      borderRadius="8px"
      width="100%"
      overflow="hidden"
      cursor="pointer"
      _hover={{
        backgroundColor: GreyGrey20
      }}
      backgroundColor={props.isSelected ? GreyGrey20 : 'transparent'}
      gridTemplateAreas={`
    "icon dot title   statusDate"
    "icon dot description statusDate"
  `}
      gridTemplateColumns="auto auto 1fr auto"
      gridTemplateRows="1fr 1fr"
      alignItems="center"
      alignContent="center"
    >
      <GridItem
        gridArea="icon"
        paddingRight="12px"
        display="flex"
        alignItems="flex-start"
        justifyContent="center"
        alignSelf="start"
        width="fit-content"
      >
        {props.icon}
      </GridItem>
      <GridItem
        gridArea="dot"
        paddingRight="8px"
        display="flex"
        alignItems="flex-start"
        justifyContent="center"
        alignSelf="start"
        paddingTop="4px"
      >
        <Box
          data-testid="watchtower.unreadIndicator"
          height="8px"
          width="8px"
          borderRadius="100%"
          backgroundColor={IndigoIndigo50}
          visibility={props.isUnread === true ? 'visible' : 'hidden'}
        />
      </GridItem>
      <GridItem
        color={isEmphasized ? GreyGrey90 : GreyGrey60}
        gridArea="title"
        display="flex"
        alignItems="center"
        overflow="hidden"
      >
        <Box
          data-testid={props['data-testid'] && `${props['data-testid']}.title`}
          whiteSpace="nowrap"
          maxWidth="100%"
          overflow="hidden"
          textOverflow="ellipsis"
          {...Primary13SemiBold}
        >
          {props.title}
        </Box>
      </GridItem>
      <GridItem
        color={isEmphasized ? GreyGrey70 : GreyGrey60}
        gridArea="description"
        overflow="hidden"
      >
        <Box
          data-testid={
            props['data-testid'] && `${props['data-testid']}.description`
          }
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          {...Primary13Regular}
        >
          {props.description}
        </Box>
      </GridItem>
      <GridItem
        gridArea="statusDate"
        paddingLeft="12px"
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
        justifyContent="center"
        gap="4px"
      >
        {props.statusIcon && (
          <Flex width="16px" height="16px" flexShrink={0}>
            {props.statusIcon}
          </Flex>
        )}
        <Box {...Primary13Regular} color={GreyGrey60}>
          {props.createdAt
            ? formatDistanceToNow(props.createdAt, {
                locale: customLocales.enGb
              })
            : null}
        </Box>
      </GridItem>
    </Grid>
  )
}

const TaskIcon = (props: { isSelected: boolean; children: ReactNode }) => {
  return (
    <Center
      borderRadius={BorderRadius4}
      overflow="hidden"
      boxSize="24px"
      background={props.isSelected ? GreyGrey30 : GreyGrey20}
      _groupHover={{ background: GreyGrey30 }}
    >
      {props.children}
    </Center>
  )
}

const LoadingIcon = () => <Skeleton height="32px" width="32px" />
const LoadingTitle = () => <Skeleton height="16px" width="60%" />
const LoadingDescription = () => <Skeleton height="14px" width="80%" />

const LoadingBase = () => {
  return (
    <InboxListItemContent
      isSelected={false}
      isUnread={false}
      icon={<LoadingIcon />}
      title={<LoadingTitle />}
      description={<LoadingDescription />}
    />
  )
}

const Loading = Object.assign(LoadingBase, {
  Icon: LoadingIcon,
  Title: LoadingTitle,
  Description: LoadingDescription
})

export const InboxListItemContent = Object.assign(ListItemContentBase, {
  Loading,
  TaskIcon
})
